import * as React from 'react';

import {
  Button,
  ConsoleDialog,
  ContentSection,
  Loading,
  SearchField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideLink, LucidePlusCircle, LucideXCircle } from 'lucide-react';

import { brokeredOrderLinkOrdersAtom } from '../../_graphql/mutationBrokeredOrderLinkOrders';
import {
  $clearLinkDialogSelection,
  $linkDialogAccountId,
  $linkDialogExistingOrderIds,
  $linkDialogOrders,
  $linkDialogSearchTerm,
  $linkDialogSelectedIds,
  $resetLinkDialog,
  $toggleLinkDialogSelection,
} from '../../_state/linkOrderDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

import { LinkOrderCard } from './LinkOrderCard';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';

type OrderType = 'purchaseOrder' | 'salesOrder';

interface LinkOrderDialogProps {
  isOpen: boolean;
  brokeredOrderId: string;
  orderType: OrderType;
  existingOrderIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
  onCreate: () => void;
}

export const LinkOrderDialog = ({
  isOpen,
  brokeredOrderId,
  orderType,
  existingOrderIds,
  onClose,
  onSuccess,
  onCreate,
}: LinkOrderDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const selectedIds = useAtomValue($linkDialogSelectedIds);
  const { orders: availableOrders, isLoading } =
    useAtomValue($linkDialogOrders);

  const [searchTerm, setSearchTerm] = useAtom($linkDialogSearchTerm);
  const [accountId, setAccountId] = useAtom($linkDialogAccountId);

  const setExistingOrderIds = useSetAtom($linkDialogExistingOrderIds);
  const resetDialog = useSetAtom($resetLinkDialog);
  const toggleSelection = useSetAtom($toggleLinkDialogSelection);
  const clearSelection = useSetAtom($clearLinkDialogSelection);
  const linkOrders = useSetAtom(brokeredOrderLinkOrdersAtom({}));

  const toast = useToast();

  // Initialize dialog state when opened
  React.useEffect(() => {
    if (isOpen) {
      resetDialog();
      setExistingOrderIds(existingOrderIds);
    }
  }, [isOpen, resetDialog, setExistingOrderIds, existingOrderIds]);

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (selectedIds.size === 0) {
      toast.error(t.orders.validation.selectOrder);
      return;
    }

    try {
      setIsSubmitting(true);
      const idsToAdd = Array.from(selectedIds);

      if (orderType === 'purchaseOrder') {
        await linkOrders({
          id: brokeredOrderId,
          addPurchaseOrderIds: idsToAdd,
        });
      } else {
        await linkOrders({
          id: brokeredOrderId,
          addSalesOrderIds: idsToAdd,
        });
      }

      toast.success(
        t.orders.toast.linkSuccess(
          selectedIds.size,
          orderType === 'purchaseOrder' ? 'purchase' : 'sales',
        ),
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.orders.toast.linkError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    linkOrders,
    brokeredOrderId,
    orderType,
    selectedIds,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const title =
    orderType === 'purchaseOrder'
      ? t.orders.link.title.purchaseOrder
      : t.orders.link.title.salesOrder;

  const createButtonLabel =
    orderType === 'purchaseOrder'
      ? t.orders.link.createPurchaseOrder
      : t.orders.link.createSalesOrder;

  const canSubmit = selectedIds.size > 0 && !isSubmitting;

  return (
    <ConsoleDialog
      className="@LinkOrderDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={title}
      content={
        <div className="space-y-4">
          <div className="flex gap-2">
            <SearchField
              value={searchTerm}
              onChange={(value) => setSearchTerm(value ?? '')}
              placeholder={t.orders.link.searchPlaceholder}
              aria-label={t.orders.link.searchPlaceholder}
              className="flex-1"
            />
            <SelectAccount
              value={accountId ?? undefined}
              onChange={(value) => setAccountId(value ?? null)}
              placeholder={t.orders.link.filterByAccount}
              className="w-60"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-neutral/60">
                {t.orders.link
                  .availableOrders(
                    orderType === 'purchaseOrder' ? 'purchase' : 'sales',
                  )
                  .toUpperCase()}{' '}
                ({availableOrders.length})
              </h3>

              {selectedIds?.size > 0 && (
                <Button
                  variant="text"
                  onClick={clearSelection}
                  className="max-h-5 bg-none p-0 rac-hover:bg-transparent"
                >
                  <LucideXCircle size={16} /> {t.orders.link.clearSelection} (
                  {selectedIds?.size})
                </Button>
              )}
            </div>
            <Button
              variant="text"
              onClick={onCreate}
              className="max-h-8 bg-none p-0 py-2 rac-hover:bg-transparent"
            >
              <LucidePlusCircle size={16} /> {createButtonLabel}
            </Button>

            <ContentSection>
              <Loading isLoading={isLoading}>
                {availableOrders.length === 0 ? (
                  <div className="py-8 text-center text-neutral/60">
                    {t.orders.link.noOrders}
                  </div>
                ) : (
                  <div className="flex max-h-125 flex-col gap-2 overflow-y-auto">
                    {availableOrders.map((order) => (
                      <LinkOrderCard
                        key={order.id}
                        orderType={orderType}
                        order={order}
                        isSelected={selectedIds.has(order.id)}
                        onClick={() => toggleSelection(order.id)}
                      />
                    ))}
                  </div>
                )}
              </Loading>
            </ContentSection>
          </div>

          {selectedIds.size > 0 && (
            <div className="text-sm text-neutral/60">
              {t.orders.link.ordersSelected(selectedIds.size)}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideLink className="size-4" />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isSubmitting
              ? t.common.loading.linking
              : t.orders.link.submitButton(selectedIds.size)}
          </Button>
        </div>
      }
    />
  );
};
